import { createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { Dayjs } from 'dayjs';
import { getGroupExamsSchedule } from 'entities/groups/api';
import { getGroupSchedule, searchGroups } from 'entities/index';

import { mapGroupSchedule, mapGroupScheduleWithoutDate } from '../../lib';
import { IGroupsState } from '../IGroupsState';

const initialState: IGroupsState = {
  groups: [],
  groupsIsLoading: false,
  groupsError: null,

  dataForGroupSchedule: [],
  groupSchedule: [],
  groupScheduleIsLoading: false,
  groupScheduleError: null,

  dataForGroupExamsSchedule: [],
  groupExamsScheduleIsLoading: false,
  groupExamsScheduleError: null,
  groupScheduleWithoutDate: [],
};

export const groupsSlice = createSlice({
  name: 'groups',
  initialState: initialState,
  reducers: {
    resetGroups: (state: IGroupsState) => {
      state.groups = [];
    },
    resetGroupSchedule: (state: IGroupsState) => {
      state.dataForGroupSchedule = [];
      state.groupSchedule = [];
    },
    resetGroupExamsSchedule: (state: IGroupsState) => {
      state.dataForGroupExamsSchedule = [];
    },
    setGroupSchedule: (
      state: IGroupsState,
      action: {
        payload: { startDate: Dayjs; endDate: Dayjs };
      },
    ) => {
      state.groupSchedule = mapGroupSchedule(
        action.payload.startDate,
        action.payload.endDate,
        state.dataForGroupSchedule ?? [],
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchGroups.fulfilled, (state: IGroupsState, action) => {
        state.groups = action.payload.result.groups;
        state.groupsIsLoading = false;
        state.groupsError = null;
      })
      .addCase(searchGroups.pending, (state: IGroupsState) => {
        state.groupsIsLoading = true;
        state.groupsError = null;
      })
      .addCase(searchGroups.rejected, (state: IGroupsState, action) => {
        state.groups = [];
        state.groupsIsLoading = false;
        state.groupScheduleError =
          (action.payload as AxiosError).response?.status !== 401
            ? (action.payload as AxiosError).message
            : '';
      })
      .addCase(getGroupSchedule.fulfilled, (state: IGroupsState, action) => {
        state.dataForGroupSchedule = action.payload.result.schedule;
        state.groupSchedule = mapGroupSchedule(
          action.payload.result.startDate,
          action.payload.result.endDate,
          action.payload.result.schedule ?? [],
        );
        state.groupScheduleWithoutDate = mapGroupScheduleWithoutDate(
          action.payload.result.schedule ?? [],
        );
        state.groupScheduleIsLoading = false;
        state.groupScheduleError = null;
      })
      .addCase(getGroupSchedule.pending, (state: IGroupsState) => {
        state.groupScheduleIsLoading = true;
        state.groupScheduleError = null;
      })
      .addCase(getGroupSchedule.rejected, (state: IGroupsState, action) => {
        state.dataForGroupSchedule = [];
        state.groupSchedule = [];
        state.groupScheduleIsLoading = false;
        state.groupScheduleError =
          (action.payload as AxiosError).response?.status !== 401
            ? (action.payload as AxiosError).message
            : '';
        state.groupScheduleWithoutDate = [];
      })
      .addCase(
        getGroupExamsSchedule.fulfilled,
        (state: IGroupsState, action) => {
          state.dataForGroupExamsSchedule = action.payload.student_exams;
          state.groupExamsScheduleIsLoading = false;
          state.groupExamsScheduleError = null;
        },
      )
      .addCase(getGroupExamsSchedule.pending, (state: IGroupsState) => {
        state.groupExamsScheduleIsLoading = true;
        state.groupExamsScheduleError = null;
      })
      .addCase(
        getGroupExamsSchedule.rejected,
        (state: IGroupsState, action) => {
          state.dataForGroupExamsSchedule = [];
          state.groupExamsScheduleIsLoading = false;
          state.groupExamsScheduleError =
            (action.payload as AxiosError).response?.status !== 401
              ? (action.payload as AxiosError).message
              : '';
        },
      )
      .addDefaultCase(() => {});
  },
});
export const {
  resetGroups,
  resetGroupSchedule,
  setGroupSchedule,
  resetGroupExamsSchedule,
} = groupsSlice.actions;
export const groupsReducer = groupsSlice.reducer;
